<template>
  <div>
    <header :class="[$style.navbar, { [$style.home]: route.name === 'home' }]">
      <div :class="$style.section">
        <a
          :class="$style.item"
          :href="!isSignedIn ? 'https://cutr.com' : '/home'"
        >
          <div class="flex items-center">
            <CutrLogo />
          </div>
        </a>
      </div>

      <template v-if="!$route.meta?.whiteLabel">
        <div
          v-if="
            store.canSwitch &&
            ['Customer', 'Maker'].includes(store.activeSessionType)
          "
          :class="$style.section"
        >
          <div :class="$style.item">
            <Badge
              :variant="
                store.activeSessionType === 'Maker' ? 'primary' : 'purple'
              "
              pill
            >
              {{ $t(`auth:orgTypes.${store.activeSessionType.toLowerCase()}`) }}
            </Badge>
          </div>
        </div>
        <div
          class="flex items-center gap-2"
          :class="$style.section"
          style="gap: var(--navItemGap)"
        >
          <router-link
            v-for="(link, index) in navLinksForUser"
            :key="index"
            :to="{ name: link.name }"
            :class="$style.item"
          >
            {{ $t(`navigation:menu.${link.title}`) }}
          </router-link>
        </div>
        <div v-if="store.isAdmin" :class="$style.section">
          <div
            :class="$style.item"
            :style="
              isAdministrationPage
                ? 'box-shadow: inset 0px -2px 0px 0px var(--neutral-800)'
                : ''
            "
          >
            <VMenu :class="$style.adminMenu">
              <a href="#" style="line-height: 2" @click.prevent
                >{{ 'Administration' }} <span data-i="arrow_drop_down"
              /></a>
              <template #popper="{ hide }">
                <menu>
                  <li v-for="(link, index) in administrationLinks" :key="index">
                    <CButton
                      variant="menu"
                      :to="{ name: link.name }"
                      :color="link.name === route.name ? 'active' : undefined"
                      @click="hide"
                    >
                      {{ $t(`navigation:menu.${link.title}`) }}
                    </CButton>
                  </li>
                </menu>
              </template>
            </VMenu>
          </div>
        </div>

        <div :class="$style.section">
          <div :class="$style.item">
            <template v-if="!isSignedIn">
              <CButton
                v-show="$route.name !== 'signin'"
                color="white"
                @click="goToRoute('signin')"
              >
                {{ $t('navigation:menu.signIn') }}
              </CButton>
              <CButton
                v-show="$route.name === 'signin'"
                color="white"
                @click="goToRoute('signup')"
              >
                {{ $t('navigation:menu.signUp') }}
              </CButton>
            </template>
            <MessagesBubble v-if="isSignedIn" style="margin-right: 0.5rem" />
            <DropdownMenu
              v-if="isSignedIn"
              :buttonsList="accountMenuLinks"
              triggerColor="white"
              triggerIcon="person"
              triggerClass="compact"
              triggerOnHover
              trigger-data-testid="account-menu"
            />
          </div>
        </div>

        <div v-show="!isSignedIn" :class="$style.section">
          <LangSelector :class="$style.item" />
        </div>

        <div :class="[$style.section, $style.mobileMenu]">
          <div :class="$style.item">
            <template v-if="!isSignedIn">
              <CButton
                v-show="$route.name !== 'signin'"
                color="white"
                @click="goToRoute('signin')"
              >
                {{ $t('navigation:menu.signIn') }}
              </CButton>
              <CButton
                v-show="$route.name === 'signin'"
                color="white"
                @click="goToRoute('signup')"
              >
                {{ $t('navigation:menu.signUp') }}
              </CButton>
            </template>
            <CButton
              color="borderless"
              :icon="isOpen ? 'close' : 'menu'"
              @click="isOpen = !isOpen"
            />
          </div>
        </div>
      </template>
      <div v-if="$route.meta?.whiteLabel" style="margin-left: auto" />
    </header>

    <MobileMenu v-if="isOpen" :onClose="() => (isOpen = false)" />
  </div>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from '@/composables/plugins';
import { useActiveUserStore } from '@/stores/user';
import { computed, ref, watchEffect } from 'vue';

import CutrLogo from '@/components/common/CutrLogo.vue';
import LangSelector from '@/components/elements/Navigation/LangSelector.vue';
import MobileMenu from '@/components/elements/Navigation/MobileMenu.vue';
import { useNavigation } from '@/components/elements/Navigation/composables';
import MessagesBubble from '@/components/elements/Notifications/MessagesBubble.vue';
import DropdownMenu from '@/components/common/DropdownMenu.vue';

const store = useActiveUserStore();
const router = useRouter();
const route = useRoute();
const isOpen = ref(false);

const { navLinks, accountMenuLinks, isSignedIn, goToRoute } = useNavigation();

watchEffect(() => {
  document.body.style.overflow = isOpen.value ? 'hidden' : 'auto';
});

router.afterEach((to, from) => {
  if (to.name !== from.name) isOpen.value = false;
});

const administrationLinks = computed(() =>
  navLinks.value.filter(({ type }) => type === 'administration')
);

const navLinksForUser = computed(() =>
  navLinks.value.filter((n) => !(n.type === 'administration'))
);

const isAdministrationPage = computed(() =>
  administrationLinks.value.some((link) => link.name === route.name)
);
</script>

<style module>
.navbar {
  --navItemGap: 1.5rem;

  top: 0;
  left: 0;
  z-index: 900;
  width: 100%;
  position: fixed;
  display: flex;
  overflow: hidden;
  gap: 1rem;
  padding: 0 1.5rem;
  justify-content: flex-end;
  min-height: var(--navbarHeight);
  background-color: var(--neutral-50);
  box-shadow: 0 1px 0 0 var(--neutral-400);

  @media (max-width: 765px) {
    --navItemGap: 1rem;

    padding: 0 0.5rem;
  }
}

/* On small screens the launchpad lets the header scroll away with the page. */
@media (max-width: 860px) {
  .home {
    position: relative;
  }
}

.section {
  a {
    text-decoration: none;
    color: var(--neutral-800);
    user-select: none;
  }

  a:hover {
    color: var(--neutral-600);
  }

  :global(.router-link-active) {
    box-shadow: inset 0 -2px 0 0 var(--neutral-800);
  }

  &:nth-child(2) {
    margin-left: auto;
  }
}

.item {
  display: flex;
  height: 100%;
  align-items: center;
  white-space: nowrap;

  a:global(.active) {
    font-weight: bold;
  }
}

.mobileMenu {
  display: none;
}

@media (max-width: 630px) {
  .section:not(:first-child) {
    display: none;
  }

  .section.mobileMenu {
    margin-left: auto;
    display: block;
  }
}
</style>
